import React, { useState, useEffect, useCallback } from 'react';
import {
  FiUser, FiMail, FiPhone, FiPlus, FiEdit2, FiTrash2, FiStar,
  FiSave, FiX, FiUserX,
} from 'react-icons/fi';
import {
  getClientContacts, addClientContact, updateClientContact, deleteClientContact,
} from '../services/api';

// The three things a contact can be on the list for. These drive who gets what once the
// send paths are wired up — deliberately checkboxes rather than something parsed out of
// the title, because job-contact roles are free text and already drift ("Site Foreman"
// vs "Site Forman" vs "Site Superintendent").
const PURPOSES = [
  { key: 'receives_billing', label: 'Billing', hint: 'Invoices and statements', color: '#059669' },
  { key: 'receives_field', label: 'Field', hint: 'En route and crew-on-site alerts', color: '#d97706' },
  { key: 'receives_status', label: 'Status', hint: 'Job status changes and deliverables', color: '#1976d2' },
];

const BLANK = {
  name: '', title: '', email: '', phone: '',
  is_primary: false, is_active: true,
  receives_billing: false, receives_field: false, receives_status: false,
  notify_email: true, notify_sms: true,
};

const CARD = {
  backgroundColor: 'white',
  borderRadius: '8px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
};

function Chip({ label, hint, color }) {
  return (
    <span
      title={hint}
      style={{
        fontSize: 11, fontWeight: 600, letterSpacing: '.02em', textTransform: 'uppercase',
        color, border: `1px solid ${color}`, borderRadius: 999, padding: '2px 8px',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}

function ClientContacts({ clientId, companyLabel }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // null = not editing; 'new' = adding; a number = editing that contact
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await getClientContacts(clientId);
      setContacts(res.data);
      setError('');
    } catch (e) {
      setError('Could not load contacts.');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const startAdd = () => { setForm(BLANK); setEditingId('new'); };
  const startEdit = (c) => { setForm({ ...BLANK, ...c }); setEditingId(c.id); };
  const cancel = () => { setEditingId(null); setForm(BLANK); };

  const save = async () => {
    if (!form.name.trim()) { alert('Name is required.'); return; }
    setSaving(true);
    try {
      if (editingId === 'new') {
        await addClientContact(clientId, form);
      } else {
        await updateClientContact(clientId, editingId, form);
      }
      await load();
      cancel();
    } catch (e) {
      alert(e.response?.data?.error || 'Could not save this contact.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    const msg = c.is_active
      ? `Remove ${c.name} from this company's contacts?\n\nIf they simply left the company, `
        + `use Edit and untick "Still with the company" instead — that keeps them on past jobs.`
      : `Permanently remove ${c.name}?`;
    if (!window.confirm(msg)) return;
    try {
      await deleteClientContact(clientId, c.id);
      await load();
    } catch (e) {
      alert('Could not remove this contact.');
    }
  };

  const check = (key, label, hint) => (
    <label key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', marginBottom: 6 }}>
      <input
        type="checkbox"
        checked={!!form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.checked })}
        style={{ marginTop: 3 }}
      />
      <span>
        <span style={{ fontWeight: 500 }}>{label}</span>
        {hint && <span style={{ display: 'block', color: '#6b7280', fontSize: 12 }}>{hint}</span>}
      </span>
    </label>
  );

  const renderForm = () => (
    <div style={{ ...CARD, padding: 20, marginBottom: 16, border: '2px solid #1976d2' }}>
      <h3 style={{ margin: '0 0 16px', fontSize: 16 }}>
        {editingId === 'new' ? 'Add Contact' : 'Edit Contact'}
      </h3>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div className="form-group">
          <label>Name *</label>
          <input
            type="text" className="form-control" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Rick Alvarez"
          />
        </div>
        <div className="form-group">
          <label>Title / Role</label>
          <input
            type="text" className="form-control" value={form.title || ''}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Site Foreman"
            list="client-contact-titles"
          />
          <datalist id="client-contact-titles">
            <option value="Owner" />
            <option value="Project Manager" />
            <option value="Site Foreman" />
            <option value="Superintendent" />
            <option value="Accounts Payable" />
            <option value="Office Manager" />
            <option value="Engineer" />
            <option value="Architect" />
          </datalist>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div className="form-group">
          <label>Email</label>
          <input
            type="email" className="form-control" value={form.email || ''}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label>Phone</label>
          <input
            type="tel" className="form-control" value={form.phone || ''}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
        </div>
      </div>

      <div style={{
        border: '1px solid #e5e7eb', borderRadius: 8, padding: '14px 16px', marginBottom: 16,
      }}>
        <label style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>
          What should this person get?
        </label>
        <p style={{ color: '#6b7280', fontSize: 13, margin: '0 0 10px' }}>
          Recorded now and used when a job pulls in this company's contacts. Automatic
          sending still goes to the client record until that's switched over.
        </p>
        {PURPOSES.map((p) => check(p.key, p.label, p.hint))}
      </div>

      <div style={{
        border: '1px solid #e5e7eb', borderRadius: 8, padding: '14px 16px', marginBottom: 16,
      }}>
        {check('is_primary', 'Primary contact for this company',
          'The main person we deal with. Only one — setting this moves it off whoever has it now.')}
        {check('is_active', 'Still with the company',
          'Untick when someone leaves. They stay on past jobs but stop being offered on new ones.')}
        {check('notify_email', 'Can be emailed')}
        {check('notify_sms', 'Can be texted')}
      </div>

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button className="btn btn-secondary" onClick={cancel} disabled={saving}>
          <FiX /> Cancel
        </button>
        <button className="btn btn-primary" onClick={save} disabled={saving}>
          <FiSave /> {saving ? 'Saving…' : 'Save Contact'}
        </button>
      </div>
    </div>
  );

  const renderContact = (c) => {
    const dim = !c.is_active;
    const purposes = PURPOSES.filter((p) => c[p.key]);
    return (
      <div
        key={c.id}
        style={{
          ...CARD,
          padding: '14px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 12,
          opacity: dim ? 0.55 : 1,
          borderLeft: c.is_primary ? '4px solid #f59e0b' : '4px solid transparent',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <strong style={{ fontSize: 15 }}>{c.name}</strong>
            {c.is_primary && (
              <span title="Primary contact" style={{ color: '#f59e0b', display: 'inline-flex' }}>
                <FiStar />
              </span>
            )}
            {dim && (
              <span style={{
                fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: '#6b7280',
                display: 'inline-flex', alignItems: 'center', gap: 4,
              }}>
                <FiUserX /> No longer here
              </span>
            )}
          </div>

          {c.title && (
            <div style={{ color: '#666', fontSize: 13, marginTop: 2 }}>{c.title}</div>
          )}

          <div style={{
            display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 6, fontSize: 14,
          }}>
            {c.email && (
              <a href={`mailto:${c.email}`} style={{ color: '#1976d2', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <FiMail size={14} /> {c.email}
              </a>
            )}
            {c.phone && (
              <a href={`tel:${c.phone}`} style={{ color: '#1976d2', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <FiPhone size={14} /> {c.phone}
              </a>
            )}
          </div>

          {(purposes.length > 0 || c.notify_email === false || c.notify_sms === false) && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
              {purposes.map((p) => (
                <Chip key={p.key} label={p.label} hint={p.hint} color={p.color} />
              ))}
              {c.notify_email === false && (
                <Chip label="No email" hint="Do not email this person" color="#9ca3af" />
              )}
              {c.notify_sms === false && (
                <Chip label="No text" hint="Do not text this person" color="#9ca3af" />
              )}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <button
            className="btn btn-ghost" onClick={() => startEdit(c)}
            title="Edit contact" style={{ padding: '6px 10px' }}
          >
            <FiEdit2 />
          </button>
          <button
            className="btn btn-ghost" onClick={() => remove(c)}
            title="Remove contact" style={{ padding: '6px 10px', color: '#dc2626' }}
          >
            <FiTrash2 />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="client-contacts" style={{ marginTop: 24 }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        flexWrap: 'wrap', gap: 10,
      }}>
        <div>
          <h2 style={{ margin: 0 }}>Contacts at {companyLabel}</h2>
          <p style={{ color: '#666', fontSize: 13, margin: '4px 0 0' }}>
            Everyone we deal with here — foreman, accounts payable, project manager. These
            stay with the company, so you don't retype them on every job.
          </p>
        </div>
        {editingId === null && (
          <button className="btn btn-primary" onClick={startAdd}>
            <FiPlus /> Add Contact
          </button>
        )}
      </div>

      <div style={{ marginTop: 16 }}>
        {editingId !== null && renderForm()}

        {loading ? (
          <p style={{ color: '#666' }}>Loading contacts…</p>
        ) : error ? (
          <p style={{ color: '#dc2626' }}>{error}</p>
        ) : contacts.length === 0 ? (
          <div style={{ ...CARD, padding: 24, textAlign: 'center', color: '#666' }}>
            <FiUser style={{ fontSize: 24, color: '#9ca3af' }} />
            <p style={{ margin: '8px 0 0' }}>No contacts yet for this company.</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            {contacts.map(renderContact)}
          </div>
        )}
      </div>
    </div>
  );
}

export default ClientContacts;
